import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import api from '../../api/client';
import { Loader2, Search, TrendingUp, Zap, Copy, RefreshCw } from 'lucide-react';

const WINDOW_OPTIONS = [
  { days: 7,  label: 'Last 7 days'  },
  { days: 30, label: 'Last 30 days' },
  { days: 90, label: 'Last 90 days' },
];

const SearchAnalyticsAdmin = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/search-analytics', { params: { days, limit: 100 } });
      setData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load search analytics');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [days]);

  const copy = (t) => { navigator.clipboard.writeText(t); toast.success(`Copied "${t}"`); };

  return (
    <div className="space-y-4" data-testid="search-analytics-panel">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-xl font-black text-[#12263A] flex items-center gap-2">
            <Search className="w-5 h-5 text-[#F46A25]" /> Search analytics
          </h2>
          <p className="text-xs text-neutral-600 mt-1 max-w-xl">
            Every keyword typed into the site's search bar is captured here. Use the "Zero results" list to spot demand for products you don't yet stock.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select value={days} onChange={e => setDays(Number(e.target.value))} className="border border-neutral-300 rounded-sm px-3 py-1.5 text-sm" data-testid="analytics-window">
            {WINDOW_OPTIONS.map(w => <option key={w.days} value={w.days}>{w.label}</option>)}
          </select>
          <button onClick={load} className="text-xs border border-neutral-300 hover:border-[#F46A25] px-3 py-1.5 rounded-sm flex items-center gap-1" data-testid="analytics-refresh">
            <RefreshCw className="w-3 h-3" /> Refresh
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-[#F46A25]" /></div>
      ) : !data ? (
        <div className="text-neutral-500 italic">No data</div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="bg-white border border-neutral-200 rounded-sm p-4">
              <div className="text-[10px] uppercase text-neutral-500 font-bold">Total searches</div>
              <div className="text-3xl font-black text-[#12263A] mt-1">{data.total_searches.toLocaleString()}</div>
            </div>
            <div className="bg-white border border-neutral-200 rounded-sm p-4">
              <div className="text-[10px] uppercase text-neutral-500 font-bold">Unique terms</div>
              <div className="text-3xl font-black text-[#12263A] mt-1">{data.top_terms.length}</div>
            </div>
            <div className="bg-white border border-red-200 rounded-sm p-4">
              <div className="text-[10px] uppercase text-red-600 font-bold">Zero-result terms</div>
              <div className="text-3xl font-black text-red-700 mt-1">{data.zero_result_terms.length}</div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Top 10 */}
            <div className="bg-white border border-neutral-200 rounded-sm">
              <div className="px-4 py-2 border-b border-neutral-200 flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-[#F46A25]" />
                <h3 className="font-bold text-[#12263A]">Top searched terms</h3>
              </div>
              <table className="w-full text-sm">
                <thead className="bg-neutral-50 text-[10px] uppercase text-neutral-600 font-bold">
                  <tr>
                    <th className="text-left px-4 py-2">#</th>
                    <th className="text-left px-4 py-2">Term</th>
                    <th className="text-right px-4 py-2">Searches</th>
                    <th className="text-right px-4 py-2">Avg results</th>
                    <th className="text-right px-4 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {data.top_terms.slice(0, 10).map((t, i) => (
                    <tr key={t.term} className="border-t border-neutral-100" data-testid={`top-term-${i}`}>
                      <td className="px-4 py-2 text-neutral-400 font-mono text-xs">{i + 1}</td>
                      <td className="px-4 py-2 font-semibold text-[#12263A]">{t.term}</td>
                      <td className="px-4 py-2 text-right font-bold">{t.count}</td>
                      <td className={`px-4 py-2 text-right ${t.avg_results === 0 ? 'text-red-600 font-bold' : 'text-neutral-600'}`}>{t.avg_results}</td>
                      <td className="px-4 py-2 text-right">
                        <button onClick={() => copy(t.term)} className="text-neutral-400 hover:text-[#F46A25]" title="Copy"><Copy className="w-3 h-3" /></button>
                      </td>
                    </tr>
                  ))}
                  {data.top_terms.length === 0 && (
                    <tr><td colSpan={5} className="text-center py-8 text-neutral-400 italic">No searches in this window yet.</td></tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Zero results */}
            <div className="bg-white border border-red-200 rounded-sm">
              <div className="px-4 py-2 border-b border-red-200 flex items-center gap-2 bg-red-50">
                <Zap className="w-4 h-4 text-red-600" />
                <h3 className="font-bold text-red-800">Searched — 0 results (unmet demand)</h3>
              </div>
              <table className="w-full text-sm">
                <thead className="bg-neutral-50 text-[10px] uppercase text-neutral-600 font-bold">
                  <tr>
                    <th className="text-left px-4 py-2">Term</th>
                    <th className="text-right px-4 py-2">Times</th>
                    <th className="text-left px-4 py-2">Last seen</th>
                    <th className="text-right px-4 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {data.zero_result_terms.slice(0, 20).map((t, i) => (
                    <tr key={t.term} className="border-t border-neutral-100" data-testid={`zero-term-${i}`}>
                      <td className="px-4 py-2 font-semibold text-[#12263A]">{t.term}</td>
                      <td className="px-4 py-2 text-right font-bold text-red-700">{t.count}</td>
                      <td className="px-4 py-2 text-neutral-500 text-xs">{t.last_seen ? new Date(t.last_seen).toLocaleDateString() : '—'}</td>
                      <td className="px-4 py-2 text-right">
                        <button onClick={() => copy(t.term)} className="text-neutral-400 hover:text-[#F46A25]" title="Copy"><Copy className="w-3 h-3" /></button>
                      </td>
                    </tr>
                  ))}
                  {data.zero_result_terms.length === 0 && (
                    <tr><td colSpan={4} className="text-center py-8 text-neutral-400 italic">Nothing has returned zero results — great catalogue coverage.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default SearchAnalyticsAdmin;
